import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Bug, Lightbulb, MapPin, MessageCircle, Send, CheckCircle2 } from "lucide-react";

const CATEGORIES = [
  { id: "bug", label: "Bug report", desc: "Something's broken or acting strange", Icon: Bug },
  { id: "feature", label: "Feature idea", desc: "A suggestion to make inAirlie better", Icon: Lightbulb },
  { id: "venue_info", label: "Venue info wrong", desc: "Hours, contact, address or photo needs fixing", Icon: MapPin },
  { id: "other", label: "Something else", desc: "Anything else on your mind", Icon: MessageCircle },
];

export default function Feedback() {
  const location = useLocation();
  const [category, setCategory] = useState("bug");
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Please add a short message so we know what's up.");
      return;
    }
    setSending(true);
    try {
      await api.post("/feedback", {
        category,
        message: message.trim(),
        name: name.trim() || null,
        email: email.trim() || null,
        page_url: (typeof window !== "undefined" ? window.location.origin : "") + (location.state?.from || ""),
      });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Couldn't send your feedback. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="feedback-thanks">
          <div className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-navy/10 text-navy mb-6">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h1 className="font-heading text-3xl lg:text-4xl text-navy tracking-tight mb-4">Thanks — message received</h1>
          <p className="text-neutral-600 leading-relaxed mb-8">
            {email
              ? "We just sent a confirmation to your inbox. If we need more info we'll reply directly."
              : "Your feedback is with our editorial team. Because you didn't leave an email we won't be able to reply, but every note is read."}
          </p>
          <Button
            onClick={() => { setSent(false); setMessage(""); setName(""); setEmail(""); setCategory("bug"); }}
            variant="outline"
            className="rounded-full"
            data-testid="send-another-btn"
          >
            Send another
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-12" data-testid="feedback-page">
        <div className="mb-10">
          <span className="text-sm tracking-widest text-coastal uppercase">Feedback</span>
          <h1 className="font-heading text-4xl lg:text-5xl text-navy tracking-tight mt-2" data-testid="feedback-title">
            Help us make inAirlie better
          </h1>
          <p className="mt-4 text-neutral-600 leading-relaxed">
            Spotted a bug? Missing a venue? Got an idea? Drop us a line below — no login required.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-8" data-testid="feedback-form">
          <div>
            <label className="text-sm font-medium text-navy block mb-3">What&apos;s this about?</label>
            <div className="grid sm:grid-cols-2 gap-3">
              {CATEGORIES.map(({ id, label, desc, Icon }) => {
                const active = category === id;
                return (
                  <button
                    type="button"
                    key={id}
                    onClick={() => setCategory(id)}
                    data-testid={`category-${id}`}
                    className={`text-left rounded-2xl p-4 border transition-all ${
                      active
                        ? "border-navy bg-navy/5 shadow-sm"
                        : "border-black/10 bg-white hover:border-navy/30 hover:bg-navy/[0.02]"
                    }`}
                  >
                    <Icon className={`w-5 h-5 mb-2 ${active ? "text-navy" : "text-coastal"}`} />
                    <div className={`font-medium ${active ? "text-navy" : "text-neutral-800"}`}>{label}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">{desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-navy block mb-2" htmlFor="fb-message">Your message</label>
            <Textarea
              id="fb-message"
              rows={7}
              placeholder="Tell us what happened, what you'd love to see, or what's wrong..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={4000}
              data-testid="feedback-message-input"
              className="resize-y"
              required
            />
            <div className="text-xs text-neutral-400 mt-1 text-right">{message.length}/4000</div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-navy block mb-2" htmlFor="fb-name">Your name <span className="text-neutral-400 font-normal">(optional)</span></label>
              <Input
                id="fb-name"
                placeholder="Jane"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                data-testid="feedback-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-navy block mb-2" htmlFor="fb-email">Your email <span className="text-neutral-400 font-normal">(optional)</span></label>
              <Input
                id="fb-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={200}
                data-testid="feedback-email-input"
              />
            </div>
          </div>
          <p className="text-xs text-neutral-500 -mt-4">
            Only add an email if you want a reply. We never share it and we don&apos;t send marketing.
          </p>

          <div className="flex items-center justify-end pt-2">
            <Button
              type="submit"
              disabled={sending || !message.trim()}
              className="rounded-full bg-navy hover:bg-navy/90 text-white px-6"
              data-testid="submit-feedback-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? "Sending..." : "Send feedback"}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
